// Translation helpers for grid field labels and validation messages.
// Field labels resolve via the `inventory-import` namespace (keys `fields.<field>`); validation
// messages carry a translation key + params (see lib/validate.ts) that consumers translate here.

import type { TFunction } from 'i18next';
import { FIELD_BY_KEY } from './targetFields';
import type { IssueParams } from './types';

/** Translation key (under `inventory-import`) for a grid field's display label. */
export function fieldLabelKey(field: string): string {
  if (field === 'product') return 'fields.product';
  if (field === 'location') return 'fields.location';
  if (field === 'lengthInches') return 'fields.length';
  return FIELD_BY_KEY[field] ? `fields.${field}` : field;
}

/** Translate a grid field's display label via the `inventory-import` namespace. */
export function fieldLabel(t: TFunction, field: string): string {
  return t(fieldLabelKey(field));
}

/**
 * Translation key for a SELLER_FIELDS key's display label. The three resolved-picker fields use
 * the same canonical labels as their grid props (descriptor → Product, locationCode → Location),
 * so labels stay consistent between the mapping step and the review grid.
 */
export function sellerFieldLabelKey(key: string): string {
  if (key === 'descriptor') return 'fields.product';
  if (key === 'locationCode') return 'fields.location';
  return `fields.${key}`;
}

/** Translate a SELLER_FIELDS key's display label via the `inventory-import` namespace. */
export function sellerFieldLabel(t: TFunction, key: string): string {
  return t(sellerFieldLabelKey(key));
}

/**
 * Translate a validation message. Any param whose value is a `fields.*` label key (e.g. the
 * numeric "must be a number" message references the field's own label) is resolved to its
 * translated label first, so the interpolated message reads naturally.
 */
export function translateIssueMessage(
  t: TFunction,
  messageKey: string,
  params?: IssueParams
): string {
  if (!params) return t(messageKey);
  const resolved: IssueParams = {};
  for (const [name, value] of Object.entries(params)) {
    resolved[name] =
      typeof value === 'string' && value.startsWith('fields.')
        ? t(value)
        : value;
  }
  return t(messageKey, resolved);
}
